// The pages before the Library: opening, signed out (sign in on the account page), locked (unlock with a passkey),
// and offline with nothing this computer can open.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { messageOf } from '@writing-suite/client/browser/session.ts';
import type { Boot } from '../store/account.ts';
import { AccountLinks } from './AccountLinks.tsx';

/** The wordmark, and at the right Settings and the profile once signed in (else the account page, to sign in). */
export function Masthead({ children, signedIn = false }: { children?: ReactNode; signedIn?: boolean }) {
  return (
    <header className="masthead">
      <a className="wordmark" href="/">
        <img src="/lockup.svg" alt="Five Rooms" height="40" />
      </a>
      <div className="masthead-links">
        {children}
        {signedIn ? (
          <AccountLinks />
        ) : (
          <nav aria-label="Account">
            <a href="/account">Account</a>
          </nav>
        )}
      </div>
    </header>
  );
}

/** A calm single-column page with its heading focused on arrival. */
export function Calm({ state, title, children }: { state: string; title: string; children: ReactNode }) {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    heading.current?.focus();
    document.title = `${title} — Five Rooms`;
  }, [title]);
  return (
    <div className="calm-page">
      <Masthead />
      <main className="calm" data-state={state}>
        <h1 ref={heading} tabIndex={-1}>
          {title}
        </h1>
        {children}
      </main>
    </div>
  );
}

export function Opening() {
  return (
    <div className="calm-page">
      <main className="calm" aria-busy="true" data-state="opening">
        <p className="lede">Opening your projects…</p>
      </main>
    </div>
  );
}

export function Gate({ state, onUnlocked, onRetry }: { state: Exclude<Boot, { kind: 'ready' }>; onUnlocked: () => void; onRetry: () => void }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (state.kind === 'signedOut') {
    return (
      <Calm state="signed-out" title="Five Rooms on the web">
        {state.message ? <p className="notice">{state.message}</p> : null}
        <p className="lede">Read the projects you’ve put on the web, here or offline. Sign in on your account page first: your passkey unlocks them.</p>
        <p className="actions">
          <a className="button primary" href="/account">
            Sign in
          </a>
        </p>
      </Calm>
    );
  }

  if (state.kind === 'offlineLocked') {
    return (
      <Calm state="offline-empty" title="You’re offline">
        <p className="lede">This computer hasn’t kept any projects you can open offline. Connect to the internet and sign in to read them.</p>
        <p className="actions">
          <button type="button" className="button secondary" onClick={onRetry}>
            Try again
          </button>
        </p>
      </Calm>
    );
  }

  const unlock = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await state.session.unlock();
      if (r.unlocked) onUnlocked();
      else setError('That passkey signed you in but couldn’t unlock your projects here. Use the account page to unlock another way.');
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Calm state="locked" title="Your projects are locked">
      <p className="lede">This browser is signed in, but it doesn’t have the key that opens your projects yet.</p>
      <p className="actions">
        <button type="button" className="button primary" onClick={unlock} disabled={busy}>
          Unlock with a passkey
        </button>
        <a className="button secondary" href="/account">
          Other ways to unlock
        </a>
      </p>
      {error ? (
        <p className="error" role="alert">
          {error}
        </p>
      ) : null}
    </Calm>
  );
}
